import React from "react";

import {
  Drawer,
  Box,
  Typography,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FolderSharedIcon from "@mui/icons-material/FolderShared";
import PodcastsIcon from "@mui/icons-material/Podcasts";
import PeopleIcon from "@mui/icons-material/People";
import MovieCreationOutlinedIcon from "@mui/icons-material/MovieCreationOutlined";
import InventoryOutlinedIcon from "@mui/icons-material/InventoryOutlined";
import CoPresentIcon from "@mui/icons-material/CoPresent";
import IosShareIcon from "@mui/icons-material/IosShare";

import { SidebarListProps } from "../types";

interface MobileSidebarProps {
  open: boolean;
  onClose: () => void;
  pageName: string;
}

export default function MobileSidebar({
  open,
  onClose,
  pageName,
}: MobileSidebarProps) {
  const sidebarList: SidebarListProps[] = [
    {
      icon: <PodcastsIcon />,
      name: "Episodes",
      link: "/episodes",
      isSelected: pageName === "Episodes",
    },
    {
      icon: <MovieCreationOutlinedIcon />,
      name: "Upload Episode",
      link: "/episodes/upload",
      isSelected: pageName === "Episode Upload",
    },
    {
      icon: <InventoryOutlinedIcon />,
      name: "Projects",
      link: "/projects",
      isSelected: pageName === "Projects",
    },
    {
      icon: <FolderSharedIcon />,
      name: "Shared Episodes",
      link: "/shared-episodes",
      disabled: true,
      isSelected: false,
      subMenu: [
        {
          icon: <IosShareIcon />,
          name: "Shared Episodes",
          link: "/shared-episodes",
          isSelected: pageName === "Shared Episodes",
        },
        {
          icon: <CoPresentIcon />,
          name: "Episodes Shared With Me",
          link: "/episodes-shared-with-me",
          isSelected: pageName === "Episodes Shared With Me",
        },
      ],
    },
  ];

  const sidebarBottomList: SidebarListProps[] = [
    {
      icon: <PeopleIcon />,
      name: "Users",
      link: "/users",
      isSelected: pageName === "Users",
    },
  ];

  return (
    <Drawer
      variant="temporary"
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      sx={{
        "& .MuiDrawer-paper": {
          width: "100vw",
          height: "100vh",
          backgroundColor: "#f8f9fa",
          border: "1px solid #e9ecef",
          borderRadius: 0,
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          position: "fixed",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          py: 2,
          minHeight: 64,
          backgroundColor: "#ffffff",
          borderBottom: "2px solid #e9ecef",
          borderTop: "1px solid #e9ecef",
          borderLeft: "1px solid #e9ecef",
          position: "relative",
          boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <img
            src="/logo.png"
            alt="Podcast Assistant Logo"
            style={{
              width: 32,
              height: 32,
              display: "block",
            }}
          />
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: "#23272E",
              fontSize: "0.95rem",
              ml: 2,
            }}
          >
            Podcast Assistant
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            color: "#666",
            backgroundColor: "#fff",
            border: "1px solid #e9ecef",
            width: 36,
            height: 36,
            "&:hover": {
              backgroundColor: "#f5f7fa",
              color: "#1976d2",
            },
          }}
        >
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      {/* Navigation Items */}
      <Box sx={{ flex: 1, overflow: "auto" }}>
        <List sx={{ py: 2, px: 2 }}>
          {sidebarList.map((item) => (
            <Box key={item.name}>
              <ListItemButton
                disabled={item.disabled}
                sx={{
                  py: 1.5,
                  px: 2,
                  mb: 1,
                  borderRadius: 2,
                  backgroundColor: item.isSelected ? "#e3f2fd" : "transparent",
                  color: item.isSelected ? "#1976d2" : "#888",
                  "&:hover": {
                    backgroundColor: item.isSelected ? "#e3f2fd" : "#f5f7fa",
                  },
                  "&.Mui-disabled": {
                    opacity: 0.5,
                    color: "#b0b0b0",
                  },
                  transition: "all 0.2s ease-in-out",
                }}
                href={item.link}
                onClick={onClose}
              >
                <ListItemIcon
                  sx={{
                    minWidth: "auto",
                    mr: 2,
                    color: "inherit",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: 32,
                    width: 32,
                    borderRadius: "50%",
                    backgroundColor: item.isSelected
                      ? "#e3f2fd"
                      : "transparent",
                    boxShadow: item.isSelected
                      ? "0 2px 8px rgba(25, 118, 210, 0.10)"
                      : "none",
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      backgroundColor: "#f5f7fa",
                    },
                    "& .MuiSvgIcon-root": {
                      fontSize: 20,
                      color: item.isSelected ? "#1976d2" : "#888",
                      transition: "color 0.2s",
                    },
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primaryTypographyProps={{
                    fontSize: 16,
                    fontWeight: item.isSelected ? 700 : 500,
                    letterSpacing: 0.2,
                    color: item.isSelected ? "#1976d2" : "#23272E",
                  }}
                  primary={item.name}
                />
              </ListItemButton>
              {item.subMenu &&
                item.subMenu.some(
                  (subMenu: SidebarListProps) => subMenu.isSelected,
                ) && (
                  <List sx={{ pl: 2 }}>
                    {item.subMenu.map((subItem: SidebarListProps) => (
                      <ListItemButton
                        key={subItem.name}
                        sx={{
                          py: 1.2,
                          px: 2,
                          mb: 0.5,
                          borderRadius: 2,
                          backgroundColor: subItem.isSelected
                            ? "#e3f2fd"
                            : "transparent",
                          color: subItem.isSelected ? "#1976d2" : "#888",
                          "&:hover": {
                            backgroundColor: subItem.isSelected
                              ? "#e3f2fd"
                              : "#f5f7fa",
                          },
                          transition: "all 0.2s ease-in-out",
                        }}
                        href={subItem.link}
                        onClick={onClose}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: "auto",
                            mr: 2,
                            color: "inherit",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            height: 28,
                            width: 28,
                            borderRadius: "50%",
                            backgroundColor: subItem.isSelected
                              ? "#e3f2fd"
                              : "transparent",
                            boxShadow: subItem.isSelected
                              ? "0 2px 8px rgba(25, 118, 210, 0.10)"
                              : "none",
                            transition: "all 0.2s ease-in-out",
                            "&:hover": {
                              backgroundColor: "#f5f7fa",
                            },
                            "& .MuiSvgIcon-root": {
                              fontSize: 18,
                              color: subItem.isSelected ? "#1976d2" : "#888",
                              transition: "color 0.2s",
                            },
                          }}
                        >
                          {subItem.icon}
                        </ListItemIcon>
                        <ListItemText
                          primaryTypographyProps={{
                            fontSize: 14,
                            fontWeight: subItem.isSelected ? 700 : 500,
                            letterSpacing: 0.2,
                            color: subItem.isSelected ? "#1976d2" : "#23272E",
                          }}
                          primary={subItem.name}
                        />
                      </ListItemButton>
                    ))}
                  </List>
                )}
            </Box>
          ))}
        </List>
      </Box>

      {/* Bottom Navigation */}
      <Box
        sx={{
          position: "sticky",
          bottom: 0,
          width: "100%",
          backgroundColor: "#ffffff",
          borderTop: "2px solid #e9ecef",
          borderLeft: "1px solid #e9ecef",
          borderRight: "1px solid #e9ecef",
          boxShadow: "0 -2px 4px rgba(0,0,0,0.05)",
        }}
      >
        <List sx={{ py: 1, px: 2 }}>
          {sidebarBottomList.map((item) => (
            <ListItemButton
              key={item.name}
              sx={{
                py: 1.5,
                px: 2,
                borderRadius: 2,
                backgroundColor: item.isSelected ? "#e3f2fd" : "transparent",
                color: item.isSelected ? "#1976d2" : "#888",
                "&:hover": {
                  backgroundColor: item.isSelected ? "#e3f2fd" : "#f5f7fa",
                },
                transition: "all 0.2s ease-in-out",
              }}
              href={item.link}
              onClick={onClose}
            >
              <ListItemIcon
                sx={{
                  minWidth: "auto",
                  mr: 2,
                  color: "inherit",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: 40,
                  width: 40,
                  borderRadius: "50%",
                  backgroundColor: item.isSelected ? "#e3f2fd" : "transparent",
                  boxShadow: item.isSelected
                    ? "0 2px 8px rgba(25, 118, 210, 0.10)"
                    : "none",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    backgroundColor: "#f5f7fa",
                  },
                  "& .MuiSvgIcon-root": {
                    fontSize: 24,
                    color: item.isSelected ? "#1976d2" : "#888",
                    transition: "color 0.2s",
                  },
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primaryTypographyProps={{
                  fontSize: 16,
                  fontWeight: item.isSelected ? 700 : 500,
                  letterSpacing: 0.2,
                  color: item.isSelected ? "#1976d2" : "#23272E",
                }}
                primary={item.name}
              />
            </ListItemButton>
          ))}
        </List>
      </Box>
    </Drawer>
  );
}
